import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setBearer } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, {}=user
  const [theme, setThemeState] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Not authenticated (401) is the expected path for guests.
      if (err?.response?.status !== 401) {
        if (process.env.NODE_ENV !== "production") console.debug("[auth] /me failed:", err?.message || err);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setBearer(data.access_token);
    setUser(data);
    return data;
  }, []);

  // iter20: Read-only Demo session. No credentials required.
  const demoLogin = useCallback(async () => {
    const { data } = await api.post("/auth/demo-login");
    if (data.access_token) setBearer(data.access_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout is best-effort; local state is cleared regardless.
      if (process.env.NODE_ENV !== "production") console.debug("[auth] logout request failed:", err?.message || err);
    }
    setBearer(null);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, demoLogin, logout, refresh: load, theme, setTheme, toggleTheme }),
    [user, login, demoLogin, logout, load, theme, setTheme, toggleTheme]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
